import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  TextInput,
  Stack,
  Text,
  Center,
  Alert,
  Box,
  Group,
  Title,
  Skeleton,
  Select,
  Pagination,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconSearch, IconFileText, IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import {
  physicianPortalService,
  triggerBlobDownload,
  type PortalCaseListItem,
  type PortalCaseStatus,
} from "../../services/physicianPortal.service";
import { PortalReportTable } from "../../components/portal/PortalReportTable";
import { PortalReportBulkActions } from "../../components/portal/PortalReportBulkActions";
import { PortalStatusCards } from "../../components/portal/PortalStatusCards";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import {
  buildPortalDateFilterRange,
  normalizePortalDatePickerRange,
  type PortalDateRange,
} from "./portalDateFilters";

export function PortalDashboardPage() {
  const navigate = useNavigate();

  // Search filter (text)
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);

  // Status filter (dropdown select)
  const [status, setStatus] = useState<string>("");

  // Physician filter (dropdown select)
  const [physicianId, setPhysicianId] = useState<string>("");

  // MRN filter (text)
  const [mrn, setMrn] = useState("");
  const [debouncedMrn] = useDebouncedValue(mrn, 400);

  // Case number filter (text)
  const [caseNumber, setCaseNumber] = useState("");
  const [debouncedCaseNumber] = useDebouncedValue(caseNumber, 400);

  // Date of Service (DOS) preset and custom ranges
  const [datePreset, setDatePreset] = useState<string>("all");
  const [dateRange, setDateRange] = useState<PortalDateRange>([null, null]);

  // Report Date preset and custom ranges
  const [reportDatePreset, setReportDatePreset] = useState<string>("all");
  const [reportDateRange, setReportDateRange] = useState<PortalDateRange>([
    null,
    null,
  ]);

  // Sorting state
  const [sortField, setSortField] = useState<string>("reportDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const labToday = useLabTodayDateOnly();

  const { dateFrom, dateTo } = useMemo(() => {
    const range = buildPortalDateFilterRange(datePreset, dateRange, labToday);
    return { dateFrom: range.from, dateTo: range.to };
  }, [datePreset, dateRange, labToday]);

  const { reportDateFrom, reportDateTo } = useMemo(() => {
    const range = buildPortalDateFilterRange(
      reportDatePreset,
      reportDateRange,
      labToday
    );
    return { reportDateFrom: range.from, reportDateTo: range.to };
  }, [labToday, reportDatePreset, reportDateRange]);

  const fetchCases = useCallback(
    () =>
      physicianPortalService.getCases({
        search: debouncedSearch || undefined,
        status: (status as PortalCaseStatus) || undefined,
        physicianId: physicianId || undefined,
        mrn: debouncedMrn || undefined,
        caseNumber: debouncedCaseNumber || undefined,
        dateFrom,
        dateTo,
        reportDateFrom,
        reportDateTo,
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
        page,
      }),
    [
      debouncedSearch,
      status,
      physicianId,
      debouncedMrn,
      debouncedCaseNumber,
      dateFrom,
      dateTo,
      reportDateFrom,
      reportDateTo,
      sortField,
      sortOrder,
      page,
    ]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "portalCases",
      debouncedSearch,
      status,
      physicianId,
      debouncedMrn,
      debouncedCaseNumber,
      dateFrom,
      dateTo,
      reportDateFrom,
      reportDateTo,
      sortField,
      sortOrder,
      page,
    ],
    queryFn: fetchCases,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const handleRowClick = useCallback(
    async (caseId: string) => {
      await physicianPortalService.markReportViewed(caseId).catch(() => {});
      navigate(`/portal/report/${caseId}`);
    },
    [navigate]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
      setSelectedIds([]); // Clear selection on filter change
      setPage(1);
    },
    []
  );

  const handleStatusChange = useCallback((value: string | null) => {
    setStatus(value || "");
    setSelectedIds([]); // Clear selection on filter change
    setPage(1);
  }, []);

  const handlePhysicianChange = useCallback((value: string | null) => {
    setPhysicianId(value || "");
    setSelectedIds([]); // Clear selection on filter change
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleMrnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setMrn(event.currentTarget.value);
      setSelectedIds([]); // Clear selection on filter change
      setPage(1);
    },
    []
  );

  const handleCaseNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCaseNumber(event.currentTarget.value);
      setSelectedIds([]); // Clear selection on filter change
      setPage(1);
    },
    []
  );

  const handleDatePresetChange = useCallback((value: string | null) => {
    const preset = value || "all";
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateRange([null, null]);
    }
    setSelectedIds([]); // Clear selection on filter change
    setPage(1);
  }, []);

  const handleDateRangeChange = useCallback((dates: DatesRangeValue) => {
    setDateRange(normalizePortalDatePickerRange(dates));
    setSelectedIds([]); // Clear selection on filter change
    if (dates[0] && dates[1]) {
      setPage(1);
    }
  }, []);

  const handleReportDatePresetChange = useCallback((value: string | null) => {
    const preset = value || "all";
    setReportDatePreset(preset);
    if (preset !== "custom") {
      setReportDateRange([null, null]);
    }
    setSelectedIds([]); // Clear selection on filter change
    setPage(1);
  }, []);

  const handleReportDateRangeChange = useCallback((dates: DatesRangeValue) => {
    setReportDateRange(normalizePortalDatePickerRange(dates));
    setSelectedIds([]); // Clear selection on filter change
    if (dates[0] && dates[1]) {
      setPage(1);
    }
  }, []);

  const handleSort = useCallback(
    (field: string) => {
      setSortOrder((prev) =>
        sortField === field && prev === "asc" ? "desc" : "asc"
      );
      setSortField(field);
      setPage(1);
    },
    [sortField]
  );

  const reports = useMemo<PortalCaseListItem[]>(
    () => data?.cases ?? [],
    [data]
  );
  const selectedReportIds = useMemo(() => {
    const reportAvailableIds = new Set(
      reports
        .filter((report) => report.reportAvailable)
        .map((report) => report.caseId)
    );
    return selectedIds.filter((caseId) => reportAvailableIds.has(caseId));
  }, [reports, selectedIds]);
  const total = data?.total ?? 0;
  const totalPages = useMemo(
    () => Math.ceil(total / (data?.pageSize || 20)),
    [total, data?.pageSize]
  );
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        debouncedSearch ||
          status ||
          physicianId ||
          debouncedMrn ||
          debouncedCaseNumber ||
          dateFrom ||
          dateTo ||
          reportDateFrom ||
          reportDateTo
      ),
    [
      debouncedSearch,
      status,
      physicianId,
      debouncedMrn,
      debouncedCaseNumber,
      dateFrom,
      dateTo,
      reportDateFrom,
      reportDateTo,
    ]
  );

  const statusOptions = useMemo(() => {
    const statuses = data?.availableStatuses || [
      "received_by_lab",
      "new",
      "viewed",
      "available",
    ];
    return [
      { value: "", label: "All Statuses" },
      ...statuses.map((s) => ({
        value: s,
        label:
          s === "received_by_lab"
            ? "Received by Lab"
            : s.charAt(0).toUpperCase() + s.slice(1),
      })),
    ];
  }, [data?.availableStatuses]);

  const physicianOptions = useMemo(() => {
    const physicians = data?.availablePhysicians ?? [];
    return [
      { value: "", label: "All Physicians" },
      ...physicians.map((p) => ({
        value: p.id,
        label: p.name,
      })),
    ];
  }, [data?.availablePhysicians]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const availableReports = reports.filter((report) => report.reportAvailable);
    setSelectedIds((prev) => {
      if (prev.length === availableReports.length) {
        return [];
      }
      return availableReports.map((report) => report.caseId);
    });
  }, [reports]);

  const handleDownloadSelected = useCallback(async () => {
    if (selectedReportIds.length === 0) return;
    setDownloading(true);
    try {
      const blob = await physicianPortalService.bulkDownloadReports({
        caseIds: selectedReportIds,
      });
      triggerBlobDownload(
        blob,
        `reports-selected-${selectedReportIds.length}.zip`
      );
      notifications.show({
        color: "green",
        title: "Download complete",
        message: `Successfully downloaded ${selectedReportIds.length} reports.`,
      });
      // Fire-and-forget marking reports as viewed
      selectedReportIds.forEach((id) => {
        physicianPortalService.markReportViewed(id).catch(() => {});
      });
      setSelectedIds([]); // Clear selection upon success
    } catch (err: any) {
      notifications.show({
        color: "red",
        title: "Download failed",
        message:
          err.message ||
          "Failed to download the selected reports. Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  }, [selectedReportIds]);

  const handleDownloadFiltered = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await physicianPortalService.bulkDownloadReports({
        filters: {
          search: debouncedSearch || undefined,
          status:
            status && status !== "received_by_lab"
              ? (status as Exclude<PortalCaseStatus, "received_by_lab">)
              : undefined,
          physicianId: physicianId || undefined,
          mrn: debouncedMrn || undefined,
          caseNumber: debouncedCaseNumber || undefined,
          dateFrom,
          dateTo,
          reportDateFrom,
          reportDateTo,
        },
      });
      triggerBlobDownload(blob, "reports-all-filtered.zip");
      notifications.show({
        color: "green",
        title: "Download complete",
        message:
          "Successfully downloaded all reports matching the current filters.",
      });
    } catch (err: any) {
      notifications.show({
        color: "red",
        title: "Download failed",
        message:
          err.message ||
          "Failed to download matching reports. Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  }, [
    debouncedSearch,
    status,
    physicianId,
    debouncedMrn,
    debouncedCaseNumber,
    dateFrom,
    dateTo,
    reportDateFrom,
    reportDateTo,
  ]);

  const handlePrintSelected = useCallback(async () => {
    if (selectedReportIds.length === 0) return;

    // Open a blank window synchronously before the async PDF generation to prevent Safari popup blocking.
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      notifications.show({
        color: "orange",
        title: "Pop-up blocked",
        message:
          "Please allow pop-ups to generate and print merged report PDFs.",
      });
      return;
    }

    setDownloading(true);
    try {
      const blob = await physicianPortalService.bulkPrintReports({
        caseIds: selectedReportIds,
      });
      const url = window.URL.createObjectURL(blob);

      // Attempt to focus and print on load (best-effort, PDF viewer behaviors vary)
      try {
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
        };
      } catch (e) {
        // Ignore potential cross-origin restrictions in some browser configurations
      }

      // Navigate the pre-opened window to the generated PDF URL
      printWindow.location.href = url;

      notifications.show({
        color: "green",
        title: "Print queue ready",
        message: `Successfully generated a merged PDF for ${selectedReportIds.length} reports.`,
      });
      // Fire-and-forget marking reports as viewed
      selectedReportIds.forEach((id) => {
        physicianPortalService.markReportViewed(id).catch(() => {});
      });
      setSelectedIds([]); // Clear selection upon success

      // Safely revoke the object URL after a delay to ensure the browser has time to load the blob.
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);
    } catch (err: any) {
      if (printWindow) {
        printWindow.close();
      }
      notifications.show({
        color: "red",
        title: "Print failed",
        message:
          err.message ||
          "Failed to generate bulk PDF for printing. Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  }, [selectedReportIds]);

  const handleRowDownload = useCallback((item: PortalCaseListItem) => {
    physicianPortalService.markReportViewed(item.caseId).catch(() => {});
    window.open(
      physicianPortalService.getReportPdfUrl(item.caseId, true),
      "_blank"
    );
  }, []);

  const handleRowPrint = useCallback((item: PortalCaseListItem) => {
    physicianPortalService.markReportViewed(item.caseId).catch(() => {});
    window.open(
      physicianPortalService.getReportPdfUrl(item.caseId, false),
      "_blank"
    );
  }, []);

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up {
          animation: fadeUp 0.35s ease forwards;
        }
      `}</style>

      <Container size="lg" py="lg">
        <Stack gap="xl">
          {/* Page header */}
          <Box
            py="xl"
            px={0}
            style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
            mb="md"
          >
            <Group justify="space-between" align="flex-end">
              <Stack gap={4}>
                <Text
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--mantine-color-teal-6)",
                  }}
                >
                  Pathology Cases
                </Text>
                <Title
                  style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontWeight: 400,
                    fontSize: "2.2rem",
                    color: "var(--mantine-color-gray-9)",
                    lineHeight: 1,
                  }}
                >
                  My Cases
                </Title>
              </Stack>
              {total > 0 && (
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {total} case{total !== 1 ? "s" : ""}
                </Text>
              )}
            </Group>
          </Box>

          {/* Status Counts summary widgets */}
          {!isLoading && !error && data?.statusCounts && (
            <PortalStatusCards
              counts={data.statusCounts}
              activeStatus={status}
              onStatusChange={handleStatusChange}
            />
          )}

          {/* Filters Bar */}
          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              placeholder="Search by patient name, case number, or physician..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={handleSearchChange}
              style={{ minWidth: 200, flex: 1 }}
              styles={{
                input: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  background: "var(--mantine-color-white)",
                },
              }}
            />
            {physicianOptions.length > 2 && (
              <Select
                placeholder="Filter by Physician"
                value={physicianId}
                onChange={handlePhysicianChange}
                data={physicianOptions}
                searchable
                style={{ width: 220 }}
                styles={{
                  input: {
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    background: "var(--mantine-color-white)",
                  },
                }}
              />
            )}
            <TextInput
              placeholder="Filter by MRN..."
              value={mrn}
              onChange={handleMrnChange}
              style={{ width: 140 }}
              styles={{
                input: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  background: "var(--mantine-color-white)",
                },
              }}
            />
            <TextInput
              placeholder="Filter by Case #..."
              value={caseNumber}
              onChange={handleCaseNumberChange}
              style={{ width: 140 }}
              styles={{
                input: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  background: "var(--mantine-color-white)",
                },
              }}
            />
            <Select
              placeholder="DOS Preset"
              data-testid="dos-preset-select"
              value={datePreset}
              onChange={handleDatePresetChange}
              data={[
                { value: "all", label: "All Dates" },
                { value: "week", label: "This Week (Last 7d)" },
                { value: "month", label: "This Month (Last 30d)" },
                { value: "year", label: "This Year (YTD)" },
                { value: "custom", label: "Custom Range" },
              ]}
              style={{ width: 185 }}
              styles={{
                input: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  background: "var(--mantine-color-white)",
                },
              }}
            />
            {datePreset === "custom" && (
              <DatePickerInput
                type="range"
                placeholder="Pick DOS range"
                value={dateRange}
                onChange={handleDateRangeChange}
                maxDate={labToday}
                clearable
                popoverProps={{
                  position: "bottom-start",
                  offset: 8,
                  withinPortal: true,
                }}
                style={{ width: 220 }}
                styles={{
                  input: {
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    background: "var(--mantine-color-white)",
                  },
                }}
              />
            )}
            <Select
              placeholder="Report Date Preset"
              data-testid="report-date-preset-select"
              value={reportDatePreset}
              onChange={handleReportDatePresetChange}
              data={[
                { value: "all", label: "All Report Dates" },
                { value: "week", label: "This Week (Last 7d)" },
                { value: "month", label: "This Month (Last 30d)" },
                { value: "year", label: "This Year (YTD)" },
                { value: "custom", label: "Custom Range" },
              ]}
              style={{ width: 185 }}
              styles={{
                input: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  background: "var(--mantine-color-white)",
                },
              }}
            />
            {reportDatePreset === "custom" && (
              <DatePickerInput
                type="range"
                placeholder="Pick report range"
                value={reportDateRange}
                onChange={handleReportDateRangeChange}
                maxDate={labToday}
                clearable
                popoverProps={{
                  position: "bottom-start",
                  offset: 8,
                  withinPortal: true,
                }}
                style={{ width: 220 }}
                styles={{
                  input: {
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    background: "var(--mantine-color-white)",
                  },
                }}
              />
            )}
            <Select
              placeholder="Filter by Status"
              value={status}
              onChange={handleStatusChange}
              data={statusOptions}
              style={{ width: 160 }}
              styles={{
                input: {
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  background: "var(--mantine-color-white)",
                },
              }}
            />
          </Group>

          {/* Error state — CONV-005: always visible */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Unable to load cases"
            >
              We couldn't load your cases. Please try again.
            </Alert>
          )}

          {/* Loading state — skeletons */}
          {isLoading && (
            <Stack gap="sm">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={50} radius="sm" />
              ))}
            </Stack>
          )}

          {/* Empty state */}
          {!isLoading && !error && reports.length === 0 && (
            <Center py={80}>
              <Stack align="center" gap="md">
                <Box
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: "var(--mantine-color-gcpBlue-0)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconFileText
                    size={28}
                    color="var(--mantine-color-gcpBlue-6)"
                  />
                </Box>
                <Stack gap={4} style={{ textAlign: "center" }}>
                  <Text
                    style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: "1.4rem",
                      color: "var(--mantine-color-gray-9)",
                    }}
                  >
                    {hasActiveFilters ? "No matching cases" : "No cases yet"}
                  </Text>
                  <Text
                    size="sm"
                    c="dimmed"
                    style={{
                      maxWidth: 280,
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                  >
                    {hasActiveFilters
                      ? "Try clearing or changing your filters."
                      : "Cases will appear here after they are received and accessioned by the lab."}
                  </Text>
                </Stack>
              </Stack>
            </Center>
          )}

          {/* Table & Bulk Actions list */}
          {!isLoading && !error && reports.length > 0 && (
            <Stack gap="md" className="fade-up">
              <PortalReportBulkActions
                selectedCount={selectedReportIds.length}
                onDownloadSelected={handleDownloadSelected}
                onDownloadFiltered={handleDownloadFiltered}
                onPrintSelected={handlePrintSelected}
                downloading={downloading}
              />
              <PortalReportTable
                items={reports}
                selectedIds={selectedReportIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                onDownload={handleRowDownload}
                onPrint={handleRowPrint}
                onRowClick={handleRowClick}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={page}
                    onChange={handlePageChange}
                    total={totalPages}
                    styles={{
                      control: {
                        fontFamily: "'IBM Plex Sans', sans-serif",
                      },
                    }}
                  />
                </Group>
              )}
            </Stack>
          )}
        </Stack>
      </Container>
    </>
  );
}

export default PortalDashboardPage;
